"use client"

import { motion } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import { Compass, Map, Clock } from "lucide-react"
import Image from "next/image";

const features = [
  {
    icon: Compass,
    title: "AI Travel Planning",
    description: "Get complete itineraries with schedules, activities, and recommendations in minutes.",
    gradient: "from-indigo-600 to-purple-600",
    bgGradient: "from-indigo-50/80 to-purple-50/80"
  },
  {
    icon: Map,
    title: "Local Recommendations",
    description: "Discover best restaurants, attractions, and hidden spots loved by locals.",
    gradient: "from-purple-600 to-pink-600",
    bgGradient: "from-purple-50/80 to-pink-50/80"
  },
  {
    icon: Clock,
    title: "Smart Scheduling",
    description: "Easily organize your days with optimized routes and timing suggestions.",
    gradient: "from-pink-600 to-rose-600",
    bgGradient: "from-pink-50/80 to-rose-50/80"
  }
]

export function Features() {
  return (
    <div className="relative py-24 sm:py-32 pt-20 bg-gradient-to-b from-white via-slate-50 to-white">
      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader 
          title="Why Choose Voyas?" 
          subtitle="Experience the future of travel planning with our innovative features"
        />
        
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mt-12 sm:mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {features.map((feature, index) => (
            <FeatureCard key={index} feature={feature} index={index} />
          ))}
        </motion.div>

        <TestimonialSection />
        <StatsSection />
      </div>
    </div>
  )
}


interface Feature {
  icon: React.ElementType;
  title: string;
  description: string;
  gradient: string;
  bgGradient: string;
}

function FeatureCard({ feature, index }: { feature: Feature; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 + 0.3 }}
    >
      <Card className="rounded-2xl bg-white border border-slate-200 shadow-md hover:shadow-2xl transform transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1 group h-full overflow-hidden relative">
        <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
        <CardContent className="p-6 sm:p-8 flex flex-col items-center text-center space-y-5 sm:space-y-6">
          <div className={`p-4 sm:p-5 rounded-2xl bg-gradient-to-br ${feature.bgGradient} group-hover:scale-110 transition-transform duration-500 shadow-lg relative overflow-hidden`}>
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-shimmer"></div>
            <feature.icon strokeWidth={2.5} className={`w-8 h-8 sm:w-10 sm:h-10 relative z-10 text-gray-700 group-hover:text-gray-900 transition-colors`} />
          </div>
          <h3 className={`text-xl sm:text-2xl font-bold bg-gradient-to-r ${feature.gradient} bg-clip-text text-transparent`}>
            {feature.title}
          </h3>
          <p className="text-gray-600 sm:text-gray-700 text-base sm:text-lg leading-relaxed">
            {feature.description}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  )
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="text-center max-w-3xl mx-auto">
      <motion.h2 
        className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4 sm:mb-6"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        {title}
      </motion.h2>
      <motion.p 
        className="text-lg sm:text-xl md:text-2xl text-gray-600 leading-relaxed"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2, duration: 0.6 }}
      >
        {subtitle}
      </motion.p>
    </div>
  )
}
function TestimonialSection() {
  const testimonials = [
    {
      content: "Voyas made planning our honeymoon absolutely effortless. The AI suggestions were spot-on!",
      author: "Joyce & Chris",
      location: "Paris Trip",
      image: "profile1.png"

    },
    {
      content: "I've never had such a seamless travel planning experience. Highly recommended!",
      author: "James L.",
      location: "Tokyo Adventure",
      image: "profile2.png"
    },
    // Add more testimonials...
  ]

  return (
    <div className="mt-20 sm:mt-24 md:mt-32">
      <SectionHeader 
        title="What Our Travelers Say" 
        subtitle="Join thousands of happy travelers who've discovered the magic of AI-powered trip planning"
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mt-12 sm:mt-16">
        {testimonials.map((testimonial, index) => (
          <motion.div
            key={index}
            className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl border border-slate-200 transition-all duration-300 hover:-translate-y-1"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.15, duration: 0.6 }}
          >
            <div className="flex items-start gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <span key={i} className="text-yellow-400 text-lg">★</span>
              ))}
            </div>
            <p className="text-base sm:text-lg text-gray-700 italic mb-6 leading-relaxed">"{testimonial.content}"</p>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-gradient-to-r from-indigo-400 to-purple-400 p-0.5">
                <div className="h-full w-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                  <Image
                    src={testimonial.image}
                    alt={`${testimonial.author}`}
                    height={44}
                    width={44}
                    className="h-11 w-11 rounded-full object-cover"
                    unoptimized={true}
                  />
                </div>
              </div>
              <div>
                <p className="font-semibold text-gray-900">{testimonial.author}</p>
                <p className="text-sm text-gray-500">{testimonial.location}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

function StatsSection() {
  const stats = [
    {
      value: "4",
      label: "Seasons Covered",
      detail: "Every activity adjusted for the season"
    },
    {
      value: "8hrs",
      label: "Planning Time Saved",
      detail: "From research to ready-to-go itinerary"
    },
    {
      value: "3-14",
      label: "Days Planned",
      detail: "Perfect for weekend trips to extended stays"
    },
    {
      value: "195",
      label: "Countries Covered",
      detail: "Every corner of the world available"
    }
  ];

  return (
    <div className="mt-20 sm:mt-24 md:mt-32">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            className="text-center p-6 rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1, duration: 0.6 }}
          >
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              {stat.value}
            </h3>
            <p className="font-semibold text-gray-800 mt-2 sm:mt-3 text-sm sm:text-base">
              {stat.label}
            </p>
            <p className="text-gray-600 text-xs sm:text-sm mt-1 sm:mt-2">
              {stat.detail}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}